import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { attribution } from "@/utils/analytics";
import { streamPost } from "@/utils/sseStream";
import { SITE_TYPES, STANDARDS_OPTIONS, TIMELINES } from "@/data/hvm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0B0F17] border border-[#26334D] focus:border-[#F4A031] text-white text-sm px-4 py-3.5 clip-notch-sm transition-colors placeholder:text-[#64748B]";
const labelCls = "block font-tech text-[10px] tracking-[0.25em] uppercase text-[#64748B] mb-2";

const initial = {
  name: "", company: "", role: "", email: "", phone: "",
  siteType: "", runLength: "", standard: "", timeline: "", message: "",
};

export const LeadForm = ({ threat }) => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [reco, setReco] = useState("");
  const [recoBusy, setRecoBusy] = useState(false);
  const [recoResult, setRecoResult] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const getReco = async () => {
    if (!reco.trim() || recoBusy) return;
    setRecoBusy(true);
    setRecoResult("");
    try {
      await streamPost(`${API}/ai/recommend`, { description: reco }, (d) => setRecoResult((r) => r + d));
    } catch {
      setRecoResult("Suggestion unavailable right now — please call 0800 322 269.");
    }
    setRecoBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.siteType) {
      toast.error("Name, email and site type are required.");
      return;
    }
    setStatus("sending");
    try {
      const threatSummary = threat?.touched
        ? `${threat.mass.toFixed(1)} t @ ${threat.speed} km/h → ${threat.kj.toLocaleString()} kJ — ${threat.band} (suggested: ${threat.product})`
        : undefined;
      const attr = attribution();
      await axios.post(`${API}/leads/site-assessment`, {
        ...form,
        ...(threatSummary ? { threatSummary } : {}),
        referrer: attr.referrer,
        utmSource: attr.utm_source,
      });
      setStatus("sent");
      toast.success("Assessment request sent — we'll be in touch within one business day.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please call 0800 322 269.");
    }
  };

  return (
    <section id="assessment" data-testid="assessment-section" className="relative py-24 sm:py-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]"
            >
              Start the scoping conversation
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-4"
            >
              Request a<br /><span className="text-[#F4A031]">site assessment.</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-7 space-y-4 text-[#94A3B8] leading-relaxed"
            >
              <p>
                Tell us the site type, the run you need to protect, and the standard your
                brief demands — or tell us you don't know yet. Either way, you'll get a
                spacing and rating recommendation from people who do this every day.
              </p>
              <div className="border border-[#26334D] clip-notch p-5 bg-[#131927]">
                <p className="font-tech text-[10px] tracking-[0.25em] uppercase text-[#64748B]">What you get back</p>
                <ul className="mt-3 space-y-2 text-sm">
                  <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-[#F4A031] shrink-0 mt-0.5" /> Product shortlist matched to your threat level</li>
                  <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-[#F4A031] shrink-0 mt-0.5" /> Spacing & foundation-depth recommendation</li>
                  <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-[#F4A031] shrink-0 mt-0.5" /> Certified rating documentation for your spec</li>
                  <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-[#F4A031] shrink-0 mt-0.5" /> Installation by certified partners in your area — local warranty &amp; servicing</li>
                </ul>
              </div>
              <div className="border border-[#26334D] clip-notch p-5 bg-[#131927]">
                <p className="font-tech text-[10px] tracking-[0.25em] uppercase text-[#64748B]">
                  Not sure what you need? — AI suggestion (Claude)
                </p>
                <textarea
                  data-testid="ai-reco-input"
                  rows={2}
                  value={reco}
                  onChange={(e) => setReco(e.target.value)}
                  placeholder="e.g. Council stadium forecourt, 30 m run, services underground…"
                  className="w-full bg-[#0B0F17] border border-[#26334D] focus:border-[#F4A031] text-white text-sm px-3.5 py-2.5 clip-notch-sm transition-colors placeholder:text-[#64748B] mt-3"
                />
                <button
                  data-testid="ai-reco-button"
                  onClick={getReco}
                  disabled={recoBusy || !reco.trim()}
                  className="mt-3 inline-flex items-center gap-2 border border-[#F4A031]/50 text-[#F4A031] hover:bg-[#F4A031] hover:text-[#0B0F17] font-tech text-[11px] tracking-widest uppercase px-4 py-2.5 clip-notch-sm transition-colors disabled:opacity-50"
                >
                  {recoBusy && <Loader2 size={13} className="animate-spin" />} Get AI suggestion
                </button>
                {recoResult && (
                  <div className="mt-4 border-l-2 border-[#F4A031] pl-4">
                    <p data-testid="ai-reco-result" className="text-sm text-[#E2E8F0] leading-relaxed whitespace-pre-line">
                      {recoResult}
                    </p>
                    <button
                      data-testid="ai-reco-use"
                      onClick={() =>
                        setForm({
                          ...form,
                          message: (form.message ? form.message + "\n\n" : "") + "AI suggestion: " + recoResult,
                        })
                      }
                      className="mt-2.5 font-tech text-[10px] tracking-widest uppercase text-[#F4A031] hover:underline"
                    >
                      Add to my message →
                    </button>
                  </div>
                )}
              </div>
              <p className="font-tech text-xs tracking-widest uppercase text-[#64748B]">
                Prefer to talk? <a href="tel:0800322269" data-testid="form-phone-link" className="text-[#F4A031]">0800 322 269</a>
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-7"
          >
            {status === "sent" ? (
              <div
                data-testid="form-success-panel"
                className="bg-[#131927] border border-[#22C55E]/40 clip-notch p-10 text-center h-full flex flex-col items-center justify-center"
              >
                <CheckCircle2 size={44} className="text-[#22C55E]" />
                <h3 className="font-display font-bold uppercase text-3xl mt-5">Request received.</h3>
                <p className="text-[#94A3B8] mt-3 max-w-md">
                  Your site assessment request is in our inbox. A HVM specialist will respond
                  within one business day with next steps.
                </p>
                <button
                  data-testid="form-send-another"
                  onClick={() => { setForm(initial); setStatus("idle"); }}
                  className="mt-7 font-tech text-xs tracking-widest uppercase text-[#F4A031] hover:underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="site-assessment-form"
                className="bg-[#131927] border border-[#26334D] clip-notch p-6 sm:p-9 tracing-beam"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="lead-name">Name *</label>
                    <input id="lead-name" data-testid="form-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-company">Company</label>
                    <input id="lead-company" data-testid="form-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Organisation Ltd" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-email">Email *</label>
                    <input id="lead-email" type="email" data-testid="form-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.co.nz" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-phone">Phone</label>
                    <input id="lead-phone" data-testid="form-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+64 ..." />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-role">Your role</label>
                    <input id="lead-role" data-testid="form-role" className={inputCls} value={form.role} onChange={set("role")} placeholder="Architect / FM / Consultant" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-site-type">Site type *</label>
                    <select id="lead-site-type" data-testid="form-site-type" className={inputCls} value={form.siteType} onChange={set("siteType")}>
                      <option value="">Select site type…</option>
                      {SITE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-run-length">Run length to protect (m)</label>
                    <input id="lead-run-length" data-testid="form-run-length" className={inputCls} value={form.runLength} onChange={set("runLength")} placeholder="e.g. 40" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-standard">Standard required</label>
                    <select id="lead-standard" data-testid="form-standard" className={inputCls} value={form.standard} onChange={set("standard")}>
                      <option value="">Select standard…</option>
                      {STANDARDS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="lead-timeline">Project timeline</label>
                    <select id="lead-timeline" data-testid="form-timeline" className={inputCls} value={form.timeline} onChange={set("timeline")}>
                      <option value="">Select timeline…</option>
                      {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="lead-message">Anything else we should know</label>
                    <textarea id="lead-message" data-testid="form-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Access constraints, buried services, aesthetic requirements, threat assessment references…" />
                  </div>
                </div>

                {threat?.touched && (
                  <div
                    data-testid="form-threat-summary"
                    className="mt-6 bg-[#0B0F17] border border-[#F4A031]/40 clip-notch-sm px-4 py-3.5"
                  >
                    <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-[#F4A031]">
                      Your modelled threat — included in this enquiry
                    </p>
                    <p className="font-grotesk font-semibold text-sm text-white mt-1.5">
                      {threat.mass.toFixed(1)} t @ {threat.speed} km/h → {threat.kj.toLocaleString()} kJ · {threat.band}
                    </p>
                    <p className="text-xs text-[#94A3B8] mt-1">Suggested: {threat.product}</p>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={status === "sending"}
                  className="group mt-7 w-full inline-flex items-center justify-center gap-3 bg-[#F4A031] hover:bg-[#D98A1F] disabled:opacity-60 text-[#0B0F17] font-grotesk font-bold px-7 py-4 clip-notch-sm transition-colors"
                >
                  {status === "sending" ? (
                    <><Loader2 size={18} className="animate-spin" /> Sending…</>
                  ) : (
                    <>Send assessment request <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
                <p className="font-tech text-[10px] tracking-widest uppercase text-[#64748B] mt-4 text-center">
                  Goes straight to our HVM team — no newsletters, no spam.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
